import React, { useEffect, useRef, useState } from "react";
import Layout from "../../Layout";
import { Icon } from "@iconify/react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import DeleteUserModal from "../../../components/formComponents/DeleteUserModal";
import { toast } from "react-toastify";
import ReactPaginate from "react-paginate";
import * as Unicons from "@iconscout/react-unicons";
import Loader from "./../../../components/Loader";
import Presentation from "./Presentation/Presentation";
import useRole from "../../../utils/useRole";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import {
  getMinioSize,
  getUpdatedImageUrlWithSize,
} from "../../../utils/helpers";
import ImgOptimized from "../../../components/ImgOptimized";
import debounce from "lodash/debounce";
import MyToggle from "../../../components/MyToggle";

const AllProjects = () => {
  const [projectData, setProjectdata] = useState([]);
  const [loading, setLoading] = useState(false);
  const [delLoading, setDelLoading] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("projects");
  const [searchQuery, setSearchQuery] = useState("");
  const [isFavorite, setIsFavorite] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const isNotFirstCall = useRef(false);
  const { isAdmin, isSuperAdmin, isUser, onlyAdmins } = useRole();

  const navigate = useNavigate();

  const fetchProjects = async (page = 1, search = "", fav = isFavorite) => {
    setLoading(true);
    try {
      const params = { page: page, limit: 16, search };
      if (fav) {
        params.isFavorite = true;
      }
      const response = await axios.get(
        `${process.env.REACT_APP_BACK_URL}/api/project-showcases`,
        {
          params,
        },
      );
      setProjectdata(response.data.results);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.currentPage);
      isNotFirstCall.current = true;
    } catch (error) {
      console.error(
        "Error fetching projects:",
        error.response ? error.response.data : error,
      );
      toast.error("Something went wrong while fetching projects...");
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchRef = useRef(
    debounce((page = 1, search = "", fav = false) => {
      setCurrentPage(1);
      setProjectdata([]);
      setLoading(true);
      fetchProjects(page, search, fav);
    }, 1000),
  );

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    };
    return new Date(dateString).toLocaleString(undefined, options);
  };

  useEffect(() => {
    fetchProjects(currentPage, searchQuery, isFavorite);
  }, [currentPage]);

  useEffect(() => {
    if (!isNotFirstCall.current) return;
    debouncedFetchRef.current(1, searchQuery, isFavorite);
    return () => debouncedFetchRef.current.cancel();
  }, [searchQuery, isFavorite]);

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected + 1);
  };

  const token = localStorage.getItem("usersdatatoken");
  const axiosConfig = {
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
  };
  const handleDelete = async () => {
    if (!deleteUserId) return;
    setDelLoading(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACK_URL}/api/project-showcases/${deleteUserId}`,
        axiosConfig,
      );
      toast.success("Project deleted successfully");
      fetchProjects(currentPage);
    } catch (error) {
      console.error(
        "Error deleting project:",
        error.response ? error.response.data : error,
      );
      toast.error("Something went wrong...");
    } finally {
      closeDeleteModal();
      setDelLoading(false);
    }
  };

  const handleToggleFavorite = async (item) => {
    const newStatus = !item.isFavorite;
    const previousProjects = [...projectData];

    // Optimistic update
    setProjectdata((prev) =>
      prev.map((p) =>
        p._id === item._id ? { ...p, isFavorite: newStatus } : p,
      ),
    );

    try {
      await axios.put(
        `${process.env.REACT_APP_BACK_URL}/api/project-showcases/${item._id}`,
        { isFavorite: newStatus },
        axiosConfig,
      );
      toast.success(
        newStatus ? "Added to favorites" : "Removed from favorites",
      );
    } catch (error) {
      console.error("Error updating favorite status:", error);
      setProjectdata(previousProjects);
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to update favorite status",
      );
    }
  };

  const openDeleteModal = (projectId) => {
    setDeleteUserId(projectId);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteUserId(null);
    setIsDeleteModalOpen(false);
  };

  return (
    <Layout ProjectShowcase={true} OnChangeSearchInput={setSearchQuery}>
      {loading ? (
        <Loader />
      ) : activeLink == "projects" ? (
        <div className="mx-auto container p-3">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Project Showcase List</h2>
            <div className="flex gap-3 items-center">
              <MyToggle
                checked={isFavorite}
                onChange={(val) => setIsFavorite(val)}
              />
              {onlyAdmins ? (
                <Link
                  to="/project-showcase/add"
                  className="bg-primary-500 text-white rounded-lg p-3 text-sm flex gap-1 items-center"
                >
                  <Icon className="text-lg" icon="fluent:add-48-filled" /> Add
                  Project
                </Link>
              ) : null}
              <Link
                to="/project-showcase"
                className="bg-primary-500 text-white rounded-lg p-3 text-sm flex gap-1 items-center"
              >
                <Icon className="text-lg" icon="mage:users" /> Public View
              </Link>
            </div>
          </div>
          {/* <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"> */}
          <ResponsiveMasonry
            columnsCountBreakPoints={{ 350: 1, 600: 2, 950: 3, 1400: 4 }}
          >
            <Masonry gutter={"1.2rem"}>
              {projectData.map((item, idx) => (
                <div
                  onClick={() =>
                    navigate(`/project-showcase/${item._id}`, {
                      state: { allProjects: projectData },
                    })
                  }
                  key={idx}
                  className="relative border p-2 rounded-xl bg-white cursor-pointer hover:scale-105 hover:bg-slate-100 transition ease-in-out duration-300 "
                >
                  {item.isLive === false && (
                    <span className="absolute top-3 left-3 text-xs text-white bg-red-600 py-1 px-2 rounded-full z-[9]">
                      Not Live
                    </span>
                  )}

                  {/* Favorite star button on top right of card */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleFavorite(item);
                    }}
                    className={`absolute top-3 right-3 size-7 grid place-content-center rounded-full z-[10] transition-all shadow-sm ${
                      item.isFavorite
                        ? "bg-amber-400 text-white hover:bg-amber-500 shadow-amber-200"
                        : "bg-white/80 text-gray-500 hover:bg-white hover:text-amber-500 backdrop-blur-sm"
                    }`}
                    title={
                      item.isFavorite
                        ? "Remove from Favorites"
                        : "Mark as Favorite"
                    }
                  >
                    <Icon
                      icon={
                        item.isFavorite
                          ? "material-symbols:star-rounded"
                          : "material-symbols:star-outline-rounded"
                      }
                      className="text-lg"
                    />
                  </button>

                  {/* {item.isLive ? "true" : "false"} */}
                  {item.sliderImages.length > 2 && (
                    <div className="flex gap-2 justify-center">
                      <div className="w-2/3">
                        <ImageWithShimmer
                          className={"w-full h-[164px] rounded-lg object-cover"}
                          loading="eager"
                          height={164}
                          src={getUpdatedImageUrlWithSize(
                            `${process.env.REACT_APP_BACK_URL}/api/minio/${item.sliderImages[0]}`,
                            [1, 1, 1],
                          )}
                          alt={"Project"}
                        />
                      </div>
                      <div className="w-1/3 flex flex-col gap-2">
                        <ImageWithShimmer
                          className={"w-full h-[77px] rounded-lg object-cover"}
                          src={getUpdatedImageUrlWithSize(
                            `${process.env.REACT_APP_BACK_URL}/api/minio/${item.sliderImages[1]}`,
                            [1, 1, 1],
                          )}
                          alt={"Project"}
                          loading="eager"
                          height={77}
                        />
                        <ImageWithShimmer
                          className={"w-full h-[77px] rounded-lg object-cover"}
                          src={getUpdatedImageUrlWithSize(
                            `${process.env.REACT_APP_BACK_URL}/api/minio/${item.sliderImages[2]}`,
                            [1, 1, 1],
                          )}
                          alt={"Project"}
                          loading="eager"
                          height={77}
                        />
                      </div>
                    </div>
                  )}
                  <div className="mt-3 flex gap-2 items-center justify-between">
                    <div className="flex-1 min-w-0 pr-2">
                      <p className="text-gray-400 text-xs">
                        {formatDate(item.createdAt)}
                      </p>
                      <h3 className="text-sm font-semibold truncate w-full text-wrap break-words line-clamp-2 ">
                        {item.title}
                      </h3>
                      {[
                        item?.buildingName,
                        item?.location?.city,
                        item?.location?.state,
                      ].some(Boolean) && (
                        <p className="text-gray-500 text-xs truncate mt-0.5">
                          {[
                            item?.buildingName,
                            item?.location?.city,
                            item?.location?.state,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 justify-end shrink-0">
                      {onlyAdmins && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              `/project-showcase/${item._id}/edit/projects`,
                            );
                          }}
                          className="bg-sky-200 size-7 grid place-content-center rounded-lg text-sky-600"
                        >
                          <Icon icon="fluent:edit-48-regular" />
                        </button>
                      )}
                      {isSuperAdmin && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteModal(item._id);
                          }}
                          className="bg-red-200 size-7 grid place-content-center rounded-lg text-red-600"
                        >
                          <Icon icon="fluent:delete-48-regular" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </Masonry>
          </ResponsiveMasonry>
          {/* </div> */}

          {projectData.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-16 h-16 mb-4 opacity-40"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M3 7h18M3 12h18M3 17h18"
                />
              </svg>
              <p className="text-lg font-semibold">No projects available</p>
              <p className="text-sm mt-1">
                Try adjusting your filters or add a new project.
              </p>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex justify-center m-5">
              <ReactPaginate
                pageCount={totalPages}
                pageRangeDisplayed={3}
                marginPagesDisplayed={1}
                previousLabel={<Unicons.UilAngleLeftB />}
                nextLabel={<Unicons.UilAngleRightB />}
                onPageChange={handlePageClick}
                containerClassName={"pagination"}
                activeClassName={"active"}
                pageClassName={"page-item"}
                pageLinkClassName={"page-link"}
                previousClassName={"page-item"}
                previousLinkClassName={"page-link"}
                nextClassName={"page-item"}
                nextLinkClassName={"page-link"}
                forcePage={currentPage - 1}
              />
            </div>
          )}
        </div>
      ) : activeLink == "materialDesign" ? (
        <div></div>
      ) : activeLink == "design" ? (
        <div></div>
      ) : activeLink == "presentation" ? (
        <Presentation />
      ) : null}

      <DeleteUserModal
        loading={delLoading}
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onDelete={handleDelete}
      />
    </Layout>
  );
};

export const ImageWithShimmer = ({ src, alt, className, height }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  return (
    <div className="relative">
      {/* Shimmer Effect */}
      {isLoading && (
        <div
          className={`${className} animate-pulse bg-gray-300 absolute inset-0 rounded-lg`}
        />
      )}

      {/* Actual Image */}
      <ImgOptimized
        className={`${className} ${
          isLoading ? "opacity-0" : "opacity-100"
        } transition-opacity duration-300`}
        src={src}
        alt={alt}
        loading="lazy"
        onLoad={() => setIsLoading(false)}
        onError={() => {
          setError(true);
          setIsLoading(false);
        }}
      />

      {/* Error State */}
      {error && (
        <div
          className={`${className} bg-gray-100 flex items-center justify-center rounded-lg`}
        >
          <span className="text-gray-400 text-sm">Failed to load image</span>
        </div>
      )}
    </div>
  );
};

const Navigation = ({ activeLink, setActiveLink }) => {
  const navigationLinks = [
    { name: "Projects", link: "projects" },
    { name: "Material Design", link: "materialDesign" },
    { name: "Design", link: "design" },
    { name: "Presentation", link: "presentation" },
  ];

  return (
    <div className="my-4 p-3 mx-auto container rounded-xl border flex justify-around">
      {navigationLinks.map((link, i) => (
        <div
          key={i}
          onClick={() => setActiveLink(link.link)}
          className={`${
            activeLink === link.link && "font-bold bg-[#008080] text-white"
          } text-center p-3 flex-1 rounded-lg cursor-pointer transition duration-300 `}
        >
          {link.name}
        </div>
      ))}
    </div>
  );
};

export default AllProjects;
